import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Camera, X, ScanSearch, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Photo-based full-label OCR modal.
 * User points phone at the whole label, taps "Fotoğraf Çek", we send it to
 * POST /api/labels/parse (Gemini Vision) which returns {barkod, isim, ebat,
 * adet, firma, gelis_tarihi, aciklama}. Parent decides what to do with it via
 * `onExtract(parsed)`.
 */
export default function LabelPhotoScanner({ open, onClose, onExtract }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      stopStream();
      return;
    }
    setError("");
    setReady(false);
    setSnapshot(null);
    setPreview(null);

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1600 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        setError(
          e?.message || "Kameraya erişilemedi. Tarayıcı izinlerini kontrol edin.",
        );
      }
    };
    start();
    return () => stopStream();
  }, [open]);

  const takePhoto = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) return;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    const data = c.toDataURL("image/jpeg", 0.85);
    setSnapshot(data);
  };

  const send = async () => {
    if (!snapshot) return;
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/labels/parse", {
        image_base64: snapshot,
        mime_type: "image/jpeg",
      });
      setPreview(data.parsed);
    } catch (e) {
      const status = e?.response?.status;
      const msg = formatApiError(e);
      if (status === 402) {
        toast.error(msg, { duration: 12000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (preview) onExtract?.(preview);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white hard border-2 border-[color:var(--border)] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-[color:var(--border)] sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <ScanSearch size={18} className="text-[color:var(--accent)]" />
            <div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                /// ETİKET OKUYUCU (OCR)
              </div>
              <div className="font-bold">Fotoğraf Çek — Tüm Alanları Al</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="label-scanner-close"
            className="p-2 hard hover:bg-[color:var(--bg)]"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="m-4 hard border border-[color:var(--error)] bg-[color:var(--error-bg)] text-[color:var(--error)] p-3 text-sm">
            {error}
          </div>
        )}

        {/* Live camera preview / snapshot */}
        <div className="bg-black w-full aspect-[4/3] relative">
          <video
            ref={videoRef}
            muted
            playsInline
            className={`w-full h-full object-cover ${snapshot ? "hidden" : ""}`}
          />
          {snapshot && (
            <img
              src={snapshot}
              alt="Etiket önizleme"
              className="w-full h-full object-contain bg-black"
              data-testid="label-scanner-snapshot"
            />
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="p-4 space-y-3">
          {!snapshot && (
            <button
              onClick={takePhoto}
              disabled={!ready}
              data-testid="label-scanner-capture"
              className="w-full h-12 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Camera size={18} strokeWidth={2.5} />
              {ready ? "Fotoğraf Çek" : "Kamera hazırlanıyor…"}
            </button>
          )}

          {snapshot && !preview && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSnapshot(null)}
                disabled={busy}
                data-testid="label-scanner-retake"
                className="h-12 hard border-2 border-[color:var(--border)] hover:bg-[color:var(--bg)] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <RotateCcw size={16} /> Tekrar Çek
              </button>
              <button
                onClick={send}
                disabled={busy}
                data-testid="label-scanner-analyze"
                className="h-12 hard bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ScanSearch size={16} strokeWidth={2.5} />
                {busy ? "Analiz ediliyor…" : "Analiz Et"}
              </button>
            </div>
          )}

          {preview && (
            <div
              data-testid="label-scanner-preview"
              className="hard border-2 border-[color:var(--primary)] bg-[color:var(--bg)] p-3 space-y-2 text-sm"
            >
              <div className="ticker text-[color:var(--text-3)]">
                /// ÇIKARILAN ALANLAR
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {[
                  ["Barkod", preview.barkod],
                  ["İsim", preview.isim],
                  ["Ebat", preview.ebat],
                  ["Adet", preview.adet],
                  ["Firma", preview.firma],
                  ["Tarih", preview.gelis_tarihi],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="ticker text-[10px] text-[color:var(--text-3)]">
                      {k}
                    </div>
                    <div className="font-mono font-bold break-words">
                      {v || "-"}
                    </div>
                  </div>
                ))}
              </div>
              {preview.aciklama && (
                <div className="pt-2 border-t border-[color:var(--border)]">
                  <div className="ticker text-[10px] text-[color:var(--text-3)]">
                    Açıklama
                  </div>
                  <div className="text-xs">{preview.aciklama}</div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setPreview(null);
                    setSnapshot(null);
                  }}
                  data-testid="label-scanner-preview-retake"
                  className="flex-1 h-10 hard border-2 border-[color:var(--border)] hover:bg-white font-semibold text-sm"
                >
                  Tekrar Çek
                </button>
                <button
                  onClick={apply}
                  disabled={!preview.barkod}
                  data-testid="label-scanner-apply"
                  className="flex-1 h-10 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider text-sm disabled:opacity-50"
                >
                  Kullan
                </button>
              </div>
              {!preview.barkod && (
                <div className="text-[10px] text-[color:var(--warning)]">
                  Barkod tespit edilemedi — tekrar çekmeyi deneyin.
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-[color:var(--text-3)]">
            Etiketi düzgün ışıkta, kare içine sığdıracak şekilde tutun. Barkod,
            isim, ebat, firma ve tarih otomatik ayrıştırılacaktır.
          </div>
        </div>
      </div>
    </div>
  );
}
